// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

/** A confidentialised value must never print as 0 — it prints as 'withheld'. */
export const WITHHELD = 'withheld';

export function num(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return WITHHELD;
  return v.toLocaleString('en-AU');
}

export function signed(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return WITHHELD;
  return `${v > 0 ? '+' : ''}${v.toLocaleString('en-AU')}`;
}

export function pct1(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return WITHHELD;
  return `${v.toFixed(1)}%`;
}

export function signedPct(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return WITHHELD;
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
}

export function growthPct(from: number | null, to: number | null): number | null {
  if (from === null || to === null || !Number.isFinite(from) || !Number.isFinite(to) || from === 0) return null;
  return (to / from - 1) * 100;
}

/**
 * Two different escaping functions are required and they are NOT
 * interchangeable. `setAttribute` takes a RAW newline; `innerHTML` needs the
 * `&#10;` entity. Using the innerHTML form with setAttribute renders a literal
 * "&#10;" in the tooltip, and using the raw form inside innerHTML collapses the
 * line break entirely.
 */
export function tipAttr(lines: string[]): string {
  return lines.join('\n');
}

export function tipHtml(lines: string[]): string {
  return lines.map(escapeHtml).join('&#10;');
}

export function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Guards every SVG coordinate: a NaN here silently voids the whole element. */
export function safe(n: number, fallback = 0): number {
  return Number.isFinite(n) ? n : fallback;
}

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

export function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function debounce<T extends (...args: never[]) => void>(fn: T, ms = 300): T {
  let t: ReturnType<typeof setTimeout> | undefined;
  return ((...args: never[]) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  }) as T;
}

/** Format an as-at date as e.g. "31 December 2025". */
export function asAtLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  return `${d} ${months[m - 1]} ${y}`;
}
